import { useEffect, useRef, useState } from 'react';

export const useScroll = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  const [dragging, setDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startScrollLeft, setStartScrollLeft] = useState(0);
  const [visible, setVisible] = useState(false);

  const [scrollInfo, setScrollInfo] = useState({
    scrollWidth: 0,
    clientWidth: 0,
    scrollLeft: 0,
  });

  const updateScrollInfo = () => {
    const c = containerRef.current;
    if (!c) return;

    const { scrollWidth, clientWidth, scrollLeft } = c;
    setScrollInfo({ scrollWidth, clientWidth, scrollLeft });

    // show scroll only if content more than container
    setVisible(scrollWidth > clientWidth);
  };

  useEffect(() => {
    updateScrollInfo();
    const c = containerRef.current;
    if (!c) return;

    c.addEventListener('scroll', updateScrollInfo);
    window.addEventListener('resize', updateScrollInfo);

    return () => {
      c.removeEventListener('scroll', updateScrollInfo);
      window.removeEventListener('resize', updateScrollInfo);
    };
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new MutationObserver(() => {
      // update if Lexical has updated content
      updateScrollInfo();
    });

    observer.observe(container, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => observer.disconnect();
  }, []);

  const onThumbMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!containerRef.current || !trackRef.current) return;

    const trackRect = trackRef.current.getBoundingClientRect();
    if (e.clientX < trackRect.left || e.clientX > trackRect.right) return;

    setDragging(true);
    setStartX(e.clientX);
    setStartScrollLeft(containerRef.current.scrollLeft);
  };

  const onMouseMove = (e: MouseEvent) => {
    if (!dragging || !containerRef.current || !trackRef.current) return;

    const { scrollWidth, clientWidth } = scrollInfo;
    const scrollableWidth = scrollWidth - clientWidth;
    const trackWidth = trackRef.current.getBoundingClientRect().width;
    const thumbPx = (clientWidth / scrollWidth) * trackWidth;
    const trackMovablePx = trackWidth - thumbPx;

    const deltaX = e.clientX - startX;
    const ratio = scrollableWidth / trackMovablePx;

    containerRef.current.scrollLeft = startScrollLeft + deltaX * ratio;
  };

  const onMouseUp = () => {
    if (dragging) setDragging(false);
  };

  useEffect(() => {
    if (dragging) {
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    } else {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [dragging]);

  const { scrollWidth, clientWidth, scrollLeft } = scrollInfo;
  const thumbWidthPercent = scrollWidth ? (clientWidth / scrollWidth) * 100 : 0;
  const thumbLeftPercent =
    scrollWidth && scrollWidth !== clientWidth
      ? (scrollLeft / (scrollWidth - clientWidth)) * (100 - thumbWidthPercent)
      : 0;

  return {
    containerRef,
    trackRef,
    onThumbMouseDown,
    thumbLeftPercent,
    thumbWidthPercent,
    visible,
  };
};
